import React from 'react'
import { FormattedMessage } from 'react-intl'
import { LearnMore } from 'src/constants/trackingEvents'
import {
  Avatar,
  Button,
  Panel,
  Format,
  List,
  Text,
  ExternalLinkButton
} from 'src/Components'
import AdditionalInfo from 'src/Components/shared/AdditionalInfo'
import { QuestionsHorizontal } from 'src/Components/shared/Questions'
import { QUESTIONS } from 'src/constants/messages'
import Phone from 'src/Components/shared/Phone'
import Email from 'src/Components/shared/Email'

import styles from './ReverseMortgagesPanel.module.scss'

const ReverseMortgagesPanel = ({
  customerProfile,
  email,
  phone,
  equityPercent,
  intl,
  toggleInfo,
  showInfo
}) => (
  <Panel data-qa='ReverseMortgagesCard' className={styles.card}>
    <Panel.Heading className={styles.heading}>
      <FormattedMessage
        id='Homeowner.reverseMortgages.heading'
        defaultMessage='Want to get rid of your monthly mortgage payment? Your estimated home equity is'
      />
    </Panel.Heading>

    <Text size='xxxl' weight='light' color='positive' className={styles.equity}>
      <Format.Percentage value={equityPercent} format='0.0' />
    </Text>

    <Text color='quiet' className={styles.description}>
      <FormattedMessage
        id='Homeowner.reverseMortgages.description'
        defaultMessage='Owners with {equity} and {age} can be eligible for a reverse mortgage loan, which can often help {perk}.'
        values={{
          equity: (
            <Text color='pink' element='span'>
              <FormattedMessage
                id='Homeowner.reverseMortgages.equity'
                defaultMessage='over 50% equity'
              />
            </Text>
          ),
          age: (
            <Text color='pink' element='span'>
              <FormattedMessage
                id='Homeowner.reverseMortgages.ageLimit'
                defaultMessage='over 62 years in age'
              />
            </Text>
          ),
          perk: (
            <Text color='green' element='span'>
              <FormattedMessage
                id='Homeowner.reverseMortgages.perk'
                defaultMessage='supplement cash flow'
              />
            </Text>
          )
        }}
      />
    </Text>

    <Button
      className={styles.button}
      onClick={toggleInfo}
      track={LearnMore}
      trackEnabled={!showInfo}
      trackOptions={{ eventLabel: 'ReverseMortgage' }}
    >
      <FormattedMessage
        id='Homeowner.reverseMortgages.expandButton'
        defaultMessage='Learn more about this'
      />
    </Button>

    <AdditionalInfo
      visible={showInfo}
      className={styles.additionalInfo}
      cardWidth
      cardBottom
    >
      <div className={styles.additionalContent}>
        <AdditionalInfo.Heading>
          <FormattedMessage
            id='Homeowner.reverseMortgages.additionalInfo'
            defaultMessage='Home Equity Conversion Mortgages are a safe plan that can give older Americans greater financial security.'
          />
        </AdditionalInfo.Heading>

        <Text size='s' weight='semibold'>
          <FormattedMessage
            id='Homeowner.reverseMortgages.keyPoints'
            defaultMessage='A few things you should know:'
          />
        </Text>

        <Text element={List.Bulleted} size='s' className={styles.list}>
          <FormattedMessage
            id='Homeowner.reverseMortgages.stayHome'
            defaultMessage="You're generally able to stay in your home for the rest of your life."
          />
          <FormattedMessage
            id='Homeowner.reverseMortgages.valueOwed'
            defaultMessage='You will never owe more than the value of the home.'
          />
          <FormattedMessage
            id='Homeowner.reverseMortgages.learnMore'
            defaultMessage='Learn more: {link}'
            values={{
              link: (
                <ExternalLinkButton
                  minimal
                  href='https://www.hud.gov/program_offices/housing/sfh/hecm/rmtopten'
                >
                  <FormattedMessage
                    id='Homeowner.reverseMortgages.hudLink'
                    defaultMessage="HUD's Top 10 Things to Know about reverse mortgages."
                  />
                </ExternalLinkButton>
              )
            }}
          />
        </Text>

        <Text color='quiet' size='s'>
          <FormattedMessage
            id='Homeowner.reverseMortgages.disclaimer'
            defaultMessage='These materials are not an advertisement and are not from HUD or FHA and were not approved by the Department or Government Agency.'
          />
        </Text>

        <section className={styles.questionContainer}>
          <QuestionsHorizontal
            className={styles.questions}
            multiline
            questionsTitle={intl.formatMessage(QUESTIONS.reverseMortgagesTitle)}
            questionTopic={intl.formatMessage(QUESTIONS.reverseMortgagesTopic)}
            questions={[
              {
                question: intl.formatMessage(QUESTIONS.eligibleHomeTypes)
              },
              {
                question: intl.formatMessage(QUESTIONS.estateLeavable)
              },
              {
                placeholder: intl.formatMessage(QUESTIONS.placeholder)
              }
            ]}
          />

          <Text element='div' weight='bold' className={styles.contact}>
            <Avatar className={styles.avatar} src={customerProfile.photoUri} />
            <FormattedMessage
              id='Homeowner.reverseMortgages.getInTouch'
              defaultMessage='Get in touch with {firstName} {lastName}:'
              values={customerProfile}
            />
          </Text>

          <List.Bulleted.Inline className={styles.contactDetails}>
            <Phone value={phone} />
            <Email value={email} />
          </List.Bulleted.Inline>
        </section>
      </div>
    </AdditionalInfo>
  </Panel>
)

export default ReverseMortgagesPanel
